import { createFileRoute } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import { RelatedServicesLinks } from "@/components/RelatedServicesLinks";
import {
  Mail,
  Send,
  Users,
  BarChart3,
  Calendar,
  Sparkles,
  Wrench,
  Truck,
  Home as HomeIcon,
  ShoppingCart,
  Briefcase,
  Heart,
} from "lucide-react";

export const Route = createFileRoute("/email-marketing-colorado")({
  head: () => ({
    meta: [
        { name: "description", content: "Colorado email marketing: newsletters, automation, and lifecycle campaigns that convert. Klaviyo, Mailchimp, and HubSpot — built by a local Colorado team." },
      { title: "Email Marketing Colorado | Newsletters & Automation" },
      {
        name: "description",
        content:
          "Email marketing for Colorado businesses — newsletters, lifecycle automation, abandoned-cart, and re-engagement. Klaviyo, Mailchimp, and HubSpot. Owner-led, transparent.",
      },
      { property: "og:title", content: "Email Marketing Colorado | Newsletters & Automation" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/email-marketing-colorado" },
    ],
  }),
  component: () => (
    <ServicePage
      slug="email-marketing"
      serviceName="Email Marketing"
      pricingRoute="/pricing"
      eyebrow="Colorado Email Marketing"
      title="Email Marketing for Colorado Businesses That Drives Repeat Revenue"
      goldWord="Repeat Revenue"
      description="Newsletters, lifecycle automation, abandoned-cart flows, and re-engagement campaigns built for Colorado businesses. Klaviyo, Mailchimp, HubSpot — done in-house, no outsourced copy."
      intro={{
        heading: "Your email list is the only marketing channel you actually own.",
        body: [
          "Google can change its algorithm. Meta can shut down ads accounts. But your email list is yours forever — and for most Colorado businesses, it's the highest-ROI channel they're underusing.",
          "We build email programs that compound: a clean list, segmented audiences, automated lifecycle flows that run while you sleep, and consistently sent newsletters that keep your business top-of-mind. No spam, no rented lists, no purchased data — just real email marketing that turns one-time customers into repeat revenue.",
        ],
      }}
      benefits={[
        "Lifecycle & automation flows",
        "Newsletter strategy & writing",
        "Segmentation & list hygiene",
        "Abandoned-cart & post-purchase",
        "Re-engagement & win-back campaigns",
        "Deliverability & domain authentication",
      ]}
      features={[
        { icon: Send, title: "Lifecycle Automation", desc: "Welcome series, post-purchase, abandoned-cart, and win-back flows that run automatically and convert." },
        { icon: Mail, title: "Newsletter Strategy", desc: "Consistent monthly or weekly newsletters written by humans — not AI fluff — to keep your audience engaged." },
        { icon: Users, title: "Segmentation", desc: "Audience segmentation by behavior, purchase history, and engagement so every send is relevant." },
        { icon: Calendar, title: "Campaign Calendar", desc: "Quarterly content calendar tied to your seasonality, promotions, and Colorado market cycles." },
        { icon: Sparkles, title: "Deliverability", desc: "SPF, DKIM, DMARC, list hygiene, and re-engagement so your emails land in the inbox — not the promotions tab." },
        { icon: BarChart3, title: "Revenue Reporting", desc: "Open rate, click rate, conversion, and revenue-per-email reports tied to real ROI." },
      ]}
      frameworkHeading="The Colorado Email Marketing Method"
      frameworkIntro="A 5-step framework for turning your email list into a compounding revenue channel."
      framework={[
        { number: "01", title: "Audit & Setup", desc: "Audit your current ESP (Klaviyo, Mailchimp, HubSpot), authenticate your domain, and clean your list." },
        { number: "02", title: "Foundation Flows", desc: "Build the must-have automated flows: welcome, post-purchase, abandoned-cart, win-back, birthday/anniversary." },
        { number: "03", title: "Segmentation Strategy", desc: "Tag and segment your audience by behavior, purchase, and engagement so every send goes to the right people." },
        { number: "04", title: "Newsletter Cadence", desc: "Set a sustainable send cadence (monthly or weekly) with a quarterly content calendar tied to your business cycles." },
        { number: "05", title: "Test, Track, Refine", desc: "A/B test subject lines, send times, and offers. Monthly reports tied to revenue, not vanity metrics." },
      ]}
      industries={[
        { icon: ShoppingCart, name: "eCommerce", desc: "Lifecycle and post-purchase flows for Colorado Shopify and Woo stores." },
        { icon: Heart, name: "Health & Wellness", desc: "Practice and clinic newsletters with HIPAA-aware list management." },
        { icon: Briefcase, name: "Professional Services", desc: "Law firms, accountants, and consultants nurturing referral relationships." },
        { icon: Wrench, name: "Home Services", desc: "Maintenance reminders, seasonal promotions, and re-engagement for past customers." },
        { icon: Truck, name: "Local Retail", desc: "Brick-and-mortar businesses driving repeat foot traffic with email." },
        { icon: HomeIcon, name: "Real Estate", desc: "Listing alerts, market updates, and past-client nurture for Colorado realtors." },
      ]}
      faqs={[
        { q: "Which email platforms do you work in?", a: "Most often Klaviyo (eCommerce), Mailchimp, ActiveCampaign, HubSpot, and Constant Contact. We pick the platform based on your business type, not the other way around." },
        { q: "Do you write the email copy?", a: "Yes — written by humans on our team, not AI. We write in your voice, with real Colorado context where it matters." },
        { q: "How often should I send to my list?", a: "Depends on your business. eCommerce typically benefits from weekly sends; service businesses usually do well with twice-monthly. We'll find the cadence your list rewards with engagement instead of unsubscribes." },
        { q: "Can you fix deliverability problems?", a: "Yes. Domain authentication (SPF, DKIM, DMARC), list cleanup, re-engagement campaigns, and proper sending warmup are all part of getting your emails into the inbox." },
        { q: "Do you only serve Colorado businesses?", a: "Yes. We're a Colorado-only agency. Every email program is built by an in-house Colorado team — no outsourced copy, no offshore campaign management." },
      ]}
      sectionsAfterPricing={<RelatedServicesLinks current="seo-services" />}
    />
  ),
});
